import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { SquaresFour } from "@phosphor-icons/react";
import { Card, CardTitle, EmptyNote } from "./MatterDashboard";
import { fetchMatterCollections } from "../../service/documentCollections";
import CollectionDetailModal from "./CollectionDetailModal";

/**
 * The matter home's answer to "where do I see the collections I saved" —
 * until now a collection only surfaced as an entry in the AI chat scope
 * dropdown, with no page anywhere that just listed what exists on this
 * matter. Clicking a row opens `CollectionDetailModal`, which is also where
 * rename/delete/remove-document/prepare live so this card can stay a
 * simple list.
 */

const List = styled.div`
  max-height: 260px;
  overflow-y: auto;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: #6b7280;

  &:hover {
    background: #f4f6fa;
  }
`;

const RowName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1f2937;
  font-size: 13.5px;
`;

const RowMeta = styled.span`
  color: #8a93a3;
  font-size: 12px;
  white-space: nowrap;
`;

const MatterCollectionsCard = ({ matterId }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCollectionId, setActiveCollectionId] = useState(null);

  const reload = useCallback(async () => {
    if (!matterId) return;
    setLoading(true);
    try {
      const data = await fetchMatterCollections(matterId);
      setCollections(data?.collections || []);
    } catch (err) {
      setCollections([]);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <Card data-testid="matter-collections-card">
      <CardTitle>Collections</CardTitle>
      {collections.length === 0 ? (
        <EmptyNote>
          {loading
            ? "Loading…"
            : 'No collections yet — select a few documents (in the file list or right-click in the folder tree) and choose "Add to collection" to group them for AI chat.'}
        </EmptyNote>
      ) : (
        <List>
          {collections.map((collection) => (
            <Row
              key={collection.id}
              type="button"
              onClick={() => setActiveCollectionId(collection.id)}
              title={collection.name}
              data-testid="collection-row"
            >
              <SquaresFour size={18} weight="regular" />
              <RowName>{collection.name}</RowName>
              <RowMeta>
                {collection.member_count} doc
                {collection.member_count === 1 ? "" : "s"}
              </RowMeta>
            </Row>
          ))}
        </List>
      )}

      <CollectionDetailModal
        open={Boolean(activeCollectionId)}
        collectionId={activeCollectionId}
        onClose={() => setActiveCollectionId(null)}
        onChanged={reload}
      />
    </Card>
  );
};

MatterCollectionsCard.propTypes = {
  matterId: PropTypes.string,
};

export default MatterCollectionsCard;
